// Client-side state for an impersonation overlay. The admin's own access token is preserved so
// "Stop" returns instantly without re-login, and the target id lets authFetch re-mint an expired
// overlay token. The overlay token itself lives in the normal access-token slot (see session.ts).
import { setAccessToken } from './session';

const ADMIN_TOKEN_KEY = 'foundry.impersonation.adminToken';
const TARGET_ID_KEY = 'foundry.impersonation.targetId';

export interface ImpersonationSession {
  adminToken: string;
  targetId: string;
}

function read(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best-effort; storage may be disabled (private browsing).
  }
}

function remove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Best-effort.
  }
}

export function getImpersonationSession(): ImpersonationSession | null {
  const adminToken = read(ADMIN_TOKEN_KEY);
  const targetId = read(TARGET_ID_KEY);
  if (adminToken === null || targetId === null) return null;
  return { adminToken, targetId };
}

export function isImpersonating(): boolean {
  return getImpersonationSession() !== null;
}

export function beginImpersonation(params: {
  overlayToken: string;
  adminToken: string;
  targetId: string;
}): void {
  write(ADMIN_TOKEN_KEY, params.adminToken);
  write(TARGET_ID_KEY, params.targetId);
  setAccessToken(params.overlayToken);
}

// Keep the preserved admin token current after it is itself refreshed mid-impersonation.
export function setImpersonationAdminToken(adminToken: string): void {
  write(ADMIN_TOKEN_KEY, adminToken);
}

// Restore the admin's own session. Returns false when there was nothing to restore.
export function endImpersonation(): boolean {
  const session = getImpersonationSession();
  remove(ADMIN_TOKEN_KEY);
  remove(TARGET_ID_KEY);
  if (session === null) return false;
  setAccessToken(session.adminToken);
  return true;
}

export function clearImpersonationSession(): void {
  remove(ADMIN_TOKEN_KEY);
  remove(TARGET_ID_KEY);
}
